import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setAccessToken } from "@/lib/api";
import { Navigate, useLocation } from "react-router-dom";

const STORAGE_USER = "rc_user";

const readStoredUser = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_USER);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeStoredUser = (u) => {
  if (typeof window === "undefined") return;
  if (u) window.localStorage.setItem(STORAGE_USER, JSON.stringify(u));
  else window.localStorage.removeItem(STORAGE_USER);
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Hydrate from localStorage to avoid flashes on hard navigation
  const initial = readStoredUser();
  const [user, setUser] = useState(initial || null);
  const [loading, setLoading] = useState(!initial);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      writeStoredUser(data);
    } catch (e) {
      setUser(false);
      writeStoredUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      writeStoredUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) || e.message };
    }
  };

  const register = async ({ email, password, name, referral_code }) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, referral_code });
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      writeStoredUser(data.user);
      // Surface bonus on next page via window var
      if (typeof window !== "undefined" && data.bonus && !data.bonus.already_claimed) {
        window.__lc_last_signup_bonus = data.bonus;
      }
      return { ok: true, user: data.user, bonus: data.bonus };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    setAccessToken(null);
    writeStoredUser(null);
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      writeStoredUser(data);
      return data;
    } catch (e) {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const ProtectedRoute = ({ children, role }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (role === "admin" && !["admin", "affiliate_manager"].includes(user.role)) {
    return <Navigate to="/app" replace />;
  }
  if (role === "consumer" && user.role !== "consumer") {
    return <Navigate to="/admin" replace />;
  }
  return children;
};
